import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { hexToRgba } from '@repo/styles';
import { getIsThaiLanguage, getServiceName } from '@repo/utils';
import styled from 'styled-components';

import { BubbleIcon, BubbleIconVariant, BubbleIconVariantType, TingleIcon } from '../Icons';

export const BlankTypeEnum = {
  // 인증
  SIGN_IN_NEED: 'SIGN_IN_NEED',
  EMAIL_VERIFICATION_SUCCESS: 'EMAIL_VERIFICATION_SUCCESS',
  EMAIL_VERIFICATION_EXPIRE: 'EMAIL_VERIFICATION_EXPIRE',
  EMAIL_VERIFICATION_DUPLICATE: 'EMAIL_VERIFICATION_DUPLICATE',
  // 버블 구매
  NO_BONUS_BUBBLE_BY_EVENT: 'NO_BONUS_BUBBLE_BY_EVENT',
  // 검색
  NO_STORY_SEARCH_BY_TAG: 'NO_STORY_SEARCH_BY_TAG',
  NO_STORY_SEARCH_BY_KEYWORD: 'NO_STORY_SEARCH_BY_KEYWORD',
  NO_PROFILE_SEARCH_BY_KEYWORD: 'NO_PROFILE_SEARCH_BY_KEYWORD',
  SEARCH_STORY: 'SEARCH_STORY',
  SEARCH_PROFILE: 'SEARCH_PROFILE',
  // 에피소드
  NO_COMMENT: 'NO_COMMENT',
  SEND_REPORT_SUCCESS: 'SEND_REPORT_SUCCESS',
  SEND_REPORT_ERROR: 'SEND_REPORT_ERROR',
  // 스튜디오
  NO_PUBLISHED_STORY: 'NO_PUBLISHED_STORY',
  NO_ASSETS: 'NO_ASSETS',
  NO_FAVORITE_ASSETS: 'NO_FAVORITE_ASSETS',
  NO_USING_ASSETS: 'NO_USING_ASSETS',
  // 마이
  NO_READ_STORY: 'NO_READ_STORY',
  NO_BOOKMARK_STORY: 'NO_BOOKMARK_STORY',
  NO_PURCHASE_STORY: 'NO_PURCHASE_STORY',
  NO_COMMUNITY: 'NO_COMMUNITY',
  NO_PUSH_NOTIFICATION: 'NO_PUSH_NOTIFICATION',
  NO_FOLLOWING_AT_MY: 'NO_FOLLOWING_AT_MY',
  NO_FOLLOWER_AT_MY: 'NO_FOLLOWER_AT_MY',
  // 유저 프로필
  NO_PUBLISHED_STORY_AT_USER_PROFILE: 'NO_PUBLISHED_STORY_AT_USER_PROFILE',
  NO_COMMUNITY_AT_USER_PROFILE: 'NO_COMMUNITY_AT_USER_PROFILE',
  NO_FOLLOWING_AT_USER_PROFILE: 'NO_FOLLOWING_AT_USER_PROFILE',
  NO_FOLLOWER_AT_USER_PROFILE: 'NO_FOLLOWER_AT_USER_PROFILE',
  // 챗봇
  NO_REVIEW_FOR_CHAT: 'NO_REVIEW_FOR_CHAT',
  NO_LIKES_LIST_FOR_PERSONA: 'NO_LIKES_LIST_FOR_PERSONA',
  NO_LIKES_LIST_FOR_UNIVERSE: 'NO_LIKES_LIST_FOR_UNIVERSE',
  NO_LIKES_LIST_FOR_SCENE: 'NO_LIKES_LIST_FOR_SCENE',
  NO_CHATS_RECENTLY: 'NO_CHATS_RECENTLY',
  NO_MY_PERSONAS: 'NO_MY_PERSONAS',
  NO_MY_UNIVERSES: 'NO_MY_UNIVERSES',
  NO_MY_SCENES: 'NO_MY_SCENES',
  NO_RECENT_PLAY_PERSONA: 'NO_RECENT_PLAY_PERSONA',
  NO_RECENT_PLAY_UNIVERSE: 'NO_RECENT_PLAY_UNIVERSE',
  NO_RECENT_PLAY_SCENE: 'NO_RECENT_PLAY_SCENE',
  NO_FAVORITES_PLAY_PERSONA: 'NO_FAVORITES_PLAY_PERSONA',
  NO_FAVORITES_PLAY_UNIVERSE: 'NO_FAVORITES_PLAY_UNIVERSE',
  NO_FAVORITES_PLAY_SCENE: 'NO_FAVORITES_PLAY_SCENE',
  NO_MY_MADE_PERSONA: 'NO_MY_MADE_PERSONA',
  NO_MY_MADE_UNIVERSE: 'NO_MY_MADE_UNIVERSE',
  NO_MY_MADE_SCENE: 'NO_MY_MADE_SCENE',
  NO_MADE_CHARACTER: 'NO_MADE_CHARACTER',
  NO_MADE_UNIVERSE: 'NO_MADE_UNIVERSE',
  NO_MADE_SCENE: 'NO_MADE_SCENE',
  NO_MADE_PERSONA: 'NO_MADE_PERSONA',
  NO_SEARCH_PERSONA: 'NO_SEARCH_PERSONA',
  NO_SEARCH_UNIVERSE: 'NO_SEARCH_UNIVERSE',
  NO_SEARCH_SCENE: 'NO_SEARCH_SCENE',
  NO_SEARCH_RESULT: 'NO_SEARCH_RESULT',
  NO_CUSTOM_SEARCH_RESULT: 'NO_CUSTOM_SEARCH_RESULT',
  NO_FILTER_RESULT: 'NO_FILTER_RESULT',
  NO_PURCHASE_HISTORY: 'NO_PURCHASE_HISTORY',
  NO_LONG_TERM_MEMORY_HISTORIES: 'NO_LONG_TERM_MEMORY_HISTORIES',
} as const;

type BlankType = keyof typeof BlankTypeEnum;

type Props = {
  type: BlankType;
  style?: React.CSSProperties;
};

export const EmptyBlankView = ({ type = BlankTypeEnum.SIGN_IN_NEED, style }: Props) => {
  const { t } = useTranslation();
  const isThaiLanguage = getIsThaiLanguage();

  const data = () => {
    switch (type) {
      case BlankTypeEnum.SIGN_IN_NEED:
        return {
          text: 'login.account_must_login',
          iconVariant: BubbleIconVariant.LOGIN,
          children: <button className="login-button">{t('login.nonlogin_users_login_button')}</button>,
        };
      case BlankTypeEnum.EMAIL_VERIFICATION_SUCCESS:
        return {
          boldText: isThaiLanguage ? 'ยืนยันสำเร็จ' : 'setting.edit_email_verify_email_template_success_message',
          text: isThaiLanguage
            ? 'ขอบคุณ! อีเมลนี้ทำการยืนยันสำเร็จแล้ว'
            : 'setting.edit_email_verify_email_template_success_text',
          iconVariant: BubbleIconVariant.CONGRATULATIONS,
        };
      case BlankTypeEnum.EMAIL_VERIFICATION_EXPIRE:
        return {
          boldText: isThaiLanguage
            ? 'อีเมลยืนยันนี้ไม่สามารถใช้ได้อีกต่อไป'
            : 'setting.edit_email_verify_email_template_fail_message',
          text: isThaiLanguage
            ? 'รหัสยืนยันหมดอายุ กรุณาลองใหม่อีกครั้ง'
            : 'setting.edit_email_verify_email_template_fail_text',
          iconVariant: BubbleIconVariant.X,
        };
      // 현재 사용 X
      case BlankTypeEnum.EMAIL_VERIFICATION_DUPLICATE:
        return {
          boldText: 'setting.edit_email_verify_email_template_expiration_message',
          text: 'setting.edit_email_re_verify_message',
          iconVariant: BubbleIconVariant.STOP,
        };
      // 버블 구매
      case BlankTypeEnum.NO_BONUS_BUBBLE_BY_EVENT:
        return {
          text: 'topup.purchase_receive_bonus',
          iconVariant: BubbleIconVariant.COIN,
        };
      // 검색
      case BlankTypeEnum.NO_STORY_SEARCH_BY_TAG:
        return {
          text: 'tag.suggestion_no_match',
          iconVariant: BubbleIconVariant.SORRY,
        };
      case BlankTypeEnum.NO_STORY_SEARCH_BY_KEYWORD:
        return {
          text: 'search.category_title_list_null_message',
          iconVariant: BubbleIconVariant.SORRY,
        };
      case BlankTypeEnum.NO_PROFILE_SEARCH_BY_KEYWORD:
        return {
          text: 'search.category_profile_null_message',
          iconVariant: BubbleIconVariant.SORRY,
        };
      case BlankTypeEnum.SEARCH_STORY:
        return {
          text: 'search.category_story_tab_msg',
          iconVariant: BubbleIconVariant.DISCOVER,
        };
      case BlankTypeEnum.SEARCH_PROFILE:
        return {
          text: 'search.category_profile_tab_msg',
          iconVariant: BubbleIconVariant.DISCOVER,
        };
      // 에피소드
      case BlankTypeEnum.NO_COMMENT:
        return {
          text: 'chapter.read_end_comment_list_null_message',
          iconVariant: BubbleIconVariant.IDEA,
        };
      case BlankTypeEnum.SEND_REPORT_SUCCESS:
        return {
          boldText: 'report.result_title',
          text: 'report.result_description',
          iconVariant: BubbleIconVariant.REPORT,
        };
      case BlankTypeEnum.SEND_REPORT_ERROR:
        return {
          boldText: 'report.error_occurred',
          text: 'report_result_fail_description',
          iconVariant: BubbleIconVariant.ERROR,
        };
      // 스튜디오
      case BlankTypeEnum.NO_PUBLISHED_STORY:
        return {
          text: 'create_edit_title.list_null_message',
          iconVariant: BubbleIconVariant.IDEA,
        };
      case BlankTypeEnum.NO_ASSETS:
        return {
          text: 'search.asset_none',
          iconVariant: BubbleIconVariant.SORRY,
        };
      case BlankTypeEnum.NO_FAVORITE_ASSETS:
        return {
          text: 'library.category_favorites_asset_add_msg',
          iconVariant: BubbleIconVariant.BOOKMARK,
        };
      case BlankTypeEnum.NO_USING_ASSETS:
        return {
          text: 'asset_browser.no_asset_in_use_message',
          iconVariant: BubbleIconVariant.SORRY,
        };
      // 마이
      case BlankTypeEnum.NO_READ_STORY:
        return {
          text: 'search.story',
          iconVariant: BubbleIconVariant.DISCOVER,
        };
      case BlankTypeEnum.NO_BOOKMARK_STORY:
        return {
          text: 'library.category_favorites_list_null_message',
          iconVariant: BubbleIconVariant.BOOKMARK,
        };
      case BlankTypeEnum.NO_PURCHASE_STORY:
        return {
          text: 'library.category_purchased_story_list_null_message',
          iconVariant: BubbleIconVariant.SHOPPING,
        };
      case BlankTypeEnum.NO_COMMUNITY:
        return {
          text: 'profile.category_community_other_post_list_null_message',
          iconVariant: BubbleIconVariant.WRITING,
        };
      case BlankTypeEnum.NO_PUSH_NOTIFICATION:
        return {
          boldText: 'notification.delete_90days_message',
          text: 'notification.delete_90days_message_part2',
          iconVariant: BubbleIconVariant.NOALERT,
        };
      case BlankTypeEnum.NO_FOLLOWING_AT_MY:
        return {
          text: 'profile.following_suggestion',
          iconVariant: BubbleIconVariant.HAPPY,
        };
      case BlankTypeEnum.NO_FOLLOWER_AT_MY:
        return {
          text: 'profile.gather_followers',
          iconVariant: BubbleIconVariant.LOVE,
        };
      // 유저 프로필
      case BlankTypeEnum.NO_PUBLISHED_STORY_AT_USER_PROFILE:
        return {
          text: 'chapter.list_null_message',
          iconVariant: BubbleIconVariant.SORRY,
        };
      case BlankTypeEnum.NO_COMMUNITY_AT_USER_PROFILE:
        return {
          text: 'profile.category_community_post_list_null_message',
          iconVariant: BubbleIconVariant.SORRY,
        };
      case BlankTypeEnum.NO_FOLLOWING_AT_USER_PROFILE:
        return {
          text: 'profile.sns_category_following_list_null_message',
          iconVariant: BubbleIconVariant.SORRY,
        };
      case BlankTypeEnum.NO_FOLLOWER_AT_USER_PROFILE:
        return {
          text: 'profile.sns_category_follower_list_null_message',
          iconVariant: BubbleIconVariant.SORRY,
        };
      // 챗봇
      case BlankTypeEnum.NO_REVIEW_FOR_CHAT:
        return {
          text: 'chat.information_reviews_empty',
          iconVariant: BubbleIconVariant.SORRY,
        };
      case BlankTypeEnum.NO_LIKES_LIST_FOR_PERSONA:
        return {
          text: 'chat.profile_my_like_find_characters',
          iconVariant: BubbleIconVariant.LOVE,
        };
      case BlankTypeEnum.NO_LIKES_LIST_FOR_UNIVERSE:
        return {
          text: 'chat.profile_my_like_find_universes',
          iconVariant: BubbleIconVariant.LOVE,
        };
      case BlankTypeEnum.NO_LIKES_LIST_FOR_SCENE:
        return {
          text: 'chat.profile_my_like_find_scenes',
          iconVariant: BubbleIconVariant.LOVE,
        };
      case BlankTypeEnum.NO_CHATS_RECENTLY:
        return {
          text: 'chat.list_empty',
          iconVariant: BubbleIconVariant.IDEA,
        };
      case BlankTypeEnum.NO_MY_PERSONAS:
        return {
          text: 'chat.create_create_character_my_characters_empty',
          iconVariant: BubbleIconVariant.IDEA,
        };
      case BlankTypeEnum.NO_MY_UNIVERSES:
        return {
          text: 'chat.create_create_universe_my_universes_empty',
          iconVariant: BubbleIconVariant.IDEA,
        };
      case BlankTypeEnum.NO_MY_SCENES:
        return {
          text: 'chat.create_create_scene_my_scenes_empty',
          iconVariant: BubbleIconVariant.IDEA,
        };
      case BlankTypeEnum.NO_RECENT_PLAY_PERSONA:
        return {
          text: 'chat.profile_my_recently_played_no_characters',
          iconVariant: BubbleIconVariant.SORRY,
        };
      case BlankTypeEnum.NO_RECENT_PLAY_UNIVERSE:
        return {
          text: 'chat.profile_my_recently_played_no_universes',
          iconVariant: BubbleIconVariant.SORRY,
        };
      case BlankTypeEnum.NO_RECENT_PLAY_SCENE:
        return {
          text: 'chat.profile_my_recently_played_no_scenes',
          iconVariant: BubbleIconVariant.SORRY,
        };
      case BlankTypeEnum.NO_FAVORITES_PLAY_PERSONA:
        return {
          text: 'chat.profile_my_favorite_no_characters',
          iconVariant: BubbleIconVariant.SORRY,
        };
      case BlankTypeEnum.NO_FAVORITES_PLAY_UNIVERSE:
        return {
          text: 'chat.profile_my_favorite_no_universes',
          iconVariant: BubbleIconVariant.SORRY,
        };
      case BlankTypeEnum.NO_FAVORITES_PLAY_SCENE:
        return {
          text: 'chat.profile_my_favorite_no_scenes',
          iconVariant: BubbleIconVariant.SORRY,
        };
      case BlankTypeEnum.NO_MY_MADE_PERSONA:
        return {
          text: 'chat.profile_my_characters_empty',
          iconVariant: BubbleIconVariant.SORRY,
        };
      case BlankTypeEnum.NO_MY_MADE_UNIVERSE:
        return {
          text: 'chat.profile_my_universes_empty',
          iconVariant: BubbleIconVariant.SORRY,
        };
      case BlankTypeEnum.NO_MY_MADE_SCENE:
        return {
          text: 'chat.profile_my_scenes_empty',
          iconVariant: BubbleIconVariant.SORRY,
        };
      case BlankTypeEnum.NO_MADE_CHARACTER:
        return {
          text: 'chat.profile_my_create_no_characters',
          iconVariant: BubbleIconVariant.SORRY,
        };
      case BlankTypeEnum.NO_MADE_UNIVERSE:
        return {
          text: 'chat.profile_my_create_no_universes',
          iconVariant: BubbleIconVariant.SORRY,
        };
      case BlankTypeEnum.NO_MADE_SCENE:
        return {
          text: 'chat.profile_my_create_no_scenes',
          iconVariant: BubbleIconVariant.SORRY,
        };
      case BlankTypeEnum.NO_MADE_PERSONA:
        return {
          text: 'chat.profile_my_create_no_personas',
          iconVariant: BubbleIconVariant.SORRY,
        };
      case BlankTypeEnum.NO_FILTER_RESULT:
        return {
          text: 'chat.tap_asset_empty',
          iconVariant: BubbleIconVariant.SORRY,
        };
      case BlankTypeEnum.NO_SEARCH_PERSONA:
        return {
          text: 'chat.create_tags_no_match_characters',
          iconVariant: BubbleIconVariant.SORRY,
        };
      case BlankTypeEnum.NO_SEARCH_UNIVERSE:
        return {
          text: 'chat.create_tags_no_match_universes',
          iconVariant: BubbleIconVariant.SORRY,
        };
      case BlankTypeEnum.NO_SEARCH_SCENE:
        return {
          text: 'chat.create_tags_no_match_scenes',
          iconVariant: BubbleIconVariant.SORRY,
        };
      case BlankTypeEnum.NO_SEARCH_RESULT:
        return {
          text: 'chat.search_no_results',
          iconVariant: BubbleIconVariant.SORRY,
        };
      case BlankTypeEnum.NO_CUSTOM_SEARCH_RESULT:
        return {
          text: 'chat.custom_search_no_results',
          iconVariant: BubbleIconVariant.SORRY,
        };
      case BlankTypeEnum.NO_PURCHASE_HISTORY:
        return {
          text: 'payment_history_payment_no_record',
          iconVariant: BubbleIconVariant.SORRY,
        };
      case BlankTypeEnum.NO_LONG_TERM_MEMORY_HISTORIES:
        return {
          text: 'chat.conversation_long_term_memory_history_empty',
          iconVariant: BubbleIconVariant.SORRY,
        };
      default:
        return { text: '' };
    }
  };

  const { boldText, text, children, iconVariant } = data();

  return (
    <ViewBox iconVariant={iconVariant} boldText={boldText} text={text} style={style}>
      {children}
    </ViewBox>
  );
};

type ViewBoxProps = {
  boldText?: string;
  text: string;
  iconVariant?: BubbleIconVariantType;
  children?: React.ReactNode;
  style?: React.CSSProperties;
};

const ViewBox = ({ boldText, text, iconVariant = BubbleIconVariant.LOGIN, children, style }: ViewBoxProps) => {
  const { t } = useTranslation();
  // const { device } = useOutletContext<OutletChildType>();
  // size={device && device.isDesktop ? 150 : 100}
  const serviceName = getServiceName();

  return (
    <SViewBoxWrapper style={style}>
      <STextContainer>
        {serviceName === 'bubblechat' && <BubbleIcon variant={iconVariant} />}
        {serviceName === 'tingle' && boldText?.startsWith('setting.edit_email_verify') && (
          <TingleIcon.Logo width={242} height={45} style={{ marginBottom: '32px' }} />
        )}
        {boldText && <p className="strong-text">{t(boldText)}</p>}
        <p>
          <Trans i18nKey={text} />
        </p>
      </STextContainer>
      {children && children}
    </SViewBoxWrapper>
  );
};

const SViewBoxWrapper = styled.div`
  padding-bottom: 90px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  .login-button {
    margin-top: 24px;
    padding: 0px 32px;
    width: fit-content;
    height: 48px;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    border-radius: 8px;
    background: ${({ theme }) => theme.color.field.background};
    box-shadow: 0px 3px 6px 0px ${({ theme }) => hexToRgba(theme.color.static.primary, 0.3)};
    color: ${({ theme }) => theme.color.text.default};
    text-align: center;
    font-family: Pretendard;
    font-size: 16px;
    font-weight: 600;
  }
`;

const STextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;

  p {
    text-align: center;
    color: ${({ theme }) => theme.color.text.default};
    ${({ theme }) => theme.font.body2_m};
  }

  > .strong-text {
    ${({ theme }) => theme.font.body1_sb};
  }
`;
